interface Insights {
	isRenting: boolean
	isRentIncludedInExpensesOthersAdditionalFixed: boolean
	isRentIncludedInExpensesOthersUncategorizedPaymentServices: boolean
}

type Estimates = {
	insights?: Insights
	userNote?: null | string
	disposableAmount: number
	income: number
	expenses: number
} & Record<string, number>

const decapitalizeFirstLetter = (string: string) => {
	return string.charAt(0).toLowerCase() + string.slice(1)
}

export const capitalizeFirstLetter = (string: string) => {
	return string.charAt(0).toUpperCase() + string.slice(1)
}

export const applySignBySection = (section: string, value: number) => {
	switch (section) {
		case 'tree.income':
			return Math.abs(value)
		case 'tree.expenses':
			return -Math.abs(value)
		default:
			return value
	}
}

type EstimatesWithTree = Exclude<
	ReturnType<typeof generateEstimatesWithBudgetAsTree>,
	undefined
>

export const generateEstimatesWithBudgetAsTree = (estimates?: Estimates) => {
	if (estimates == null) return
	const {
		insights,
		userNote,
		disposableAmount,
		income,
		expenses,
		...budgetPoints
	} = estimates
	const tree = new Map<string, Map<string, Map<'total' | string, number>>>()
	for (const point in budgetPoints) {
		if (Object.prototype.hasOwnProperty.call(budgetPoints, point)) {
			const [budgetSection, capitalizedCategory, ...rest] =
				point.split(/(?=[A-Z])/)
			let category = decapitalizeFirstLetter(capitalizedCategory!)
			let categoryPointName = decapitalizeFirstLetter(rest.join(''))

			switch (categoryPointName) {
				case 'andGross':
				case 'grants':
					category = `${category}${rest.join('')}`
					categoryPointName = null as any
			}

			const value = budgetPoints[point]
			if (!tree.has(budgetSection!)) {
				tree.set(budgetSection!, new Map())
			}
			const section = tree.get(budgetSection!)
			if (!section?.has(category)) {
				section?.set(category, new Map())
			}

			const categoryMap = section?.get(category)
			if (categoryMap != null) {
				categoryMap.set(
					!categoryPointName ? 'total' : categoryPointName,
					applySignBySection(budgetSection!, value!),
				)
				section?.set(category, categoryMap)
			}
		}
	}
	const output: Record<
		string,
		Record<string, number | Record<string, number>>
	> = {}
	for (const [sectionName, sectionValues] of tree.entries()) {
		if (output[sectionName] == null) {
			output[sectionName] = {}
		}
		switch (sectionName) {
			case 'income':
				output[sectionName].total = income
				break
			case 'expenses':
				output[sectionName].total = expenses
				break
		}
		for (const [categoryName, categoryValues] of sectionValues.entries()) {
			if (output[sectionName][categoryName] == null) {
				output[sectionName][categoryName] = {}
			}
			if (categoryValues.size === 1 && categoryValues.has('total')) {
				;(output as any)[sectionName][categoryName] =
					categoryValues.get('total')
				continue
			}
			categoryValues.forEach((pointValue, pointName) => {
				;(output as any)[sectionName][categoryName][pointName] = pointValue
			})
		}
	}
	return { insights, userNote, disposableAmount, tree: output }
}

export const flattenTreeToEstimatesUpdate = (
	item: EstimatesWithTree['tree'],
) => {
	const res: Record<string, number> = {}
	const recursivelyFlatten = (obj: Record<string, any>, current?: string) => {
		for (const key in obj) {
			const value = obj[key]
			const newKey = current ? `${current}${capitalizeFirstLetter(key)}` : key
			if (value && typeof value === 'object') {
				recursivelyFlatten(value, newKey) // it's a nested object, so do it again
			} else {
				if (current != null && key === 'total') {
					res[current] = value
					continue
				}
				res[newKey] = value // it's not an object, so set the property
			}
		}
	}
	recursivelyFlatten(item)
	return res
}
